import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/Common";
import { AttachmentsInline } from "@/components/EmailComposer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Rocket, CheckCircle2, AlertTriangle, X, Copy, ExternalLink,
  Store, Users, Newspaper, ChevronDown, ChevronRight, Mail, Bot, PenLine, Save, Send,
  Eye, Globe, Wand2, Trash2, Archive,
} from "lucide-react";

const MODULE_ICON = { rivenditori: Store, ambassador: Users, redazionali: Newspaper };
const MODULE_LABEL = { rivenditori: "Rivenditori", ambassador: "Ambassador", redazionali: "Redazionali" };
const DEST_LABEL = { rivenditori: "Rivenditori", ambassador: "Ambassador Salvati", redazionali: "Testate" };
const RUNNING = ["queued", "searching", "drafting"];

/* ---------------- Toolbar condivisa: Salva selezionati + Scarta selezionati ---------------- */
function SelectionActions({ job, ids, onSaved, onDiscarded, disabled }) {
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const n = ids.length;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/autopilot/jobs/${job._id}/save-selected`, { ids });
      const dest = data.destination || DEST_LABEL[job.module] || "archivio";
      toast.success(`${data.saved} ${data.saved === 1 ? "contatto salvato" : "contatti salvati"} in ${dest}`, {
        action: onSaved?.navigate ? { label: `Vedi ${dest}`, onClick: () => onSaved.navigate() } : undefined,
      });
      onSaved?.done?.(ids);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const discard = async () => {
    setConfirm(false); setDiscarding(true);
    try {
      const { data } = await api.post(`/autopilot/jobs/${job._id}/discard-selected`, { ids });
      const word = data.discarded === 1 ? "proposta scartata" : "proposte scartate";
      toast.success(data.job_closed
        ? `${data.discarded} ${word} · coda chiusa`
        : `${data.discarded} ${word} · ${data.remaining} ancora in coda`);
      onDiscarded?.(data, ids);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Scarto fallito"); }
    finally { setDiscarding(false); }
  };

  return (
    <>
      <Button size="sm" variant="secondary" onClick={save} disabled={disabled || n === 0 || saving} data-testid="autopilot-save-selected"
        className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
        {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Salva contatti selezionati
      </Button>
      <Button size="sm" variant="danger" onClick={() => setConfirm(true)} disabled={disabled || n === 0 || discarding} data-testid="autopilot-discard-selected"
        className="h-8 rounded-full text-xs">
        {discarding ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1 h-3.5 w-3.5" />} Scarta selezionati{n > 0 ? ` (${n})` : ""}
      </Button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="autopilot-discard-dialog" className="border-border bg-brema-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Scartare {n} {n === 1 ? "proposta" : "proposte"} selezionate?</AlertDialogTitle>
            <AlertDialogDescription>
              Le righe spuntate vengono rimosse dalla coda insieme ai contatti trovati dall'Autopilot e mai salvati. Le altre proposte restano invariate. I contatti già salvati in {DEST_LABEL[job.module] || "archivio"} non vengono toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="autopilot-discard-cancel" className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={discard} data-testid="autopilot-discard-confirm" className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">Sì, scarta selezionati</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const normUrl = (u) => {
  const s = (u || "").trim();
  if (!s) return "";
  return s.startsWith("http") ? s : "https://" + s.replace(/^\/+/, "");
};
const cleanHost = (u) => (u || "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/+$/, "");

/* ---------------- Real email preview (body + firma + GDPR) ---------------- */
function EmailPreviewDialog({ open, onOpenChange, payload }) {
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !payload) return;
    setLoading(true); setHtml("");
    api.post("/email/preview", {
      subject: payload.subject || "", body: payload.body || "",
      nation: payload.nation || null, recipient_name: payload.recipient_name || "",
      informal: !!payload.informal, campaign_id: payload.campaign_id || null,
      target: payload.target || null, contact_id: payload.contact_id || null,
    }).then(({ data }) => { setHtml(data.html || ""); setSubject(data.subject || payload.subject || ""); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Anteprima non disponibile"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="autopilot-preview-dialog" className="max-w-2xl border-border bg-brema-card">
        <DialogHeader>
          <DialogTitle className="text-foreground">Anteprima email reale</DialogTitle>
          <DialogDescription>Esattamente come la riceverà il destinatario — oggetto, testo così com'è nell'editor, intestazione, firma ufficiale e nota GDPR. Nessuna modifica o traduzione all'invio.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border bg-white">
          <div className="border-b border-border px-4 py-2 text-xs text-[#555]"><b>Oggetto:</b> {subject || "—"}</div>
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Generazione anteprima…</div>
          ) : (
            <iframe title="email-preview" data-testid="autopilot-preview-iframe" srcDoc={html}
              className="h-[52vh] w-full rounded-b-lg bg-white" sandbox="" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Bulk edit dialog (template + allegati + campi) ---------------- */
function BulkEditDialog({ open, onOpenChange, module, kind, count, contactedCount = 0, initialAttachments = [], onApply }) {
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubject(""); setBodyText(""); setAttachments(initialAttachments || []);
    api.get(`/send-templates?module=${module}`).then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x._id === id);
    if (!t) return;
    if (t.subject) setSubject(t.subject);
    if (t.body) setBodyText(t.body);
    if (Array.isArray(t.attachments) && t.attachments.length) {
      setAttachments((prev) => {
        const seen = new Set(prev.map((a) => a.storage_path));
        return [...prev, ...t.attachments.filter((a) => a.storage_path && !seen.has(a.storage_path))];
      });
    }
    toast.success(`Modello applicato: ${t.title}`);
  };

  const apply = async () => {
    if (!subject.trim() && !bodyText.trim() && !(attachments || []).length) {
      toast.error("Inserisci un oggetto/testo o aggiungi un allegato"); return;
    }
    setSaving(true);
    try { await onApply({ subject: subject.trim(), body: bodyText.trim(), attachments: attachments || [] }); onOpenChange(false); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="autopilot-bulk-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-brema-card">
        <DialogHeader>
          <DialogTitle className="text-foreground">Modifica massiva · {count} contatti</DialogTitle>
          <DialogDescription>
            Applica lo stesso {kind === "email" ? "oggetto/testo e allegati" : "messaggio e allegati"} a tutti i contatti selezionati. Usa i segnaposto <code className="rounded bg-brema-bg px-1">{"{nome}"}</code>, <code className="rounded bg-brema-bg px-1">{"{citta}"}</code>, <code className="rounded bg-brema-bg px-1">{"{regione}"}</code>, <code className="rounded bg-brema-bg px-1">{"{categoria}"}</code>: verranno personalizzati per ciascun destinatario all'invio.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {contactedCount > 0 && (
            <div data-testid="autopilot-bulk-contacted-warning" className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span><b>{contactedCount}</b> tra i contatti selezionati risultano <b>già contattati</b> in precedenza. Verifica prima di inviare per evitare invii doppi o spam.</span>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Carica da Archivio Modelli / Moduli AI</Label>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger className="h-9 text-xs" data-testid="autopilot-bulk-template"><SelectValue placeholder="Carica da Archivio Modelli…" /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <SelectItem value="none" disabled>Nessun modello salvato</SelectItem>
                ) : templates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {kind === "email" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Oggetto (lascia vuoto per non cambiarlo)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="autopilot-bulk-subject" placeholder="Es. Nuova collezione Brema per {citta}" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">{kind === "email" ? "Testo email" : "Messaggio"} {kind === "email" ? "(lascia vuoto per non cambiarlo)" : ""}</Label>
            <Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} data-testid="autopilot-bulk-body"
              placeholder={"Ciao {nome},\n\n..."} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Allegati campagna (Catalogo PDF Brema o file personalizzato)</Label>
            <AttachmentsInline attachments={attachments}
              onAdd={(r) => setAttachments((p) => [...p, r])}
              onRemove={(sp) => setAttachments((p) => p.filter((a) => a.storage_path !== sp))}
              testidPrefix="autopilot-bulk" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="autopilot-bulk-cancel" className="rounded-full border border-border bg-transparent hover:bg-brema-hover">Annulla</Button>
          <Button onClick={apply} disabled={saving} data-testid="autopilot-bulk-apply" className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Applico…</> : <><Wand2 className="mr-2 h-4 w-4" /> Applica a {count}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Batch queue (Rivenditori + Redazionali) ---------------- */
function BatchQueue({ job, onResolved, onSaved, onJobPatched }) {
  const [batch, setBatch] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [editId, setEditId] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);

  const loadBatch = async (keepSel = true) => {
    if (!job.batch_id) return;
    try {
      const { data } = await api.get(`/campaign-batches/${job.batch_id}`);
      setBatch(data);
      setSelected((prev) => {
        if (keepSel && prev.size > 0) return prev;
        const sendable = (data.messages || []).filter((m) => m.status !== "inviata" && (m.recipient || "").trim());
        return new Set(sendable.map((m) => m._id));
      });
    } catch { /* ignore */ }
  };
  useEffect(() => { loadBatch(false); /* eslint-disable-next-line */ }, [job.batch_id]);

  const msgs = batch?.messages || [];
  const pending = msgs.filter((m) => m.status !== "inviata");
  const sendable = pending.filter((m) => (m.recipient || "").trim());
  const selectedIds = pending.filter((m) => selected.has(m._id)).map((m) => m._id);
  const selCount = selectedIds.length;
  const sendSelIds = sendable.filter((m) => selected.has(m._id)).map((m) => m._id);
  const sendSelCount = sendSelIds.length;
  const contactedCount = sendable.filter((m) => selected.has(m._id) && m.already_contacted).length;
  const allSel = pending.length > 0 && pending.every((m) => selected.has(m._id));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(pending.map((m) => m._id)));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExp = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const startEdit = (m) => {
    setEditId(m._id); setEditSubject(m.subject || ""); setEditBody(m.email_body || "");
    setExpanded((s) => new Set(s).add(m._id));
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/campaigns/${editId}`, { subject: editSubject, email_body: editBody });
      toast.success("Bozza aggiornata");
      await loadBatch();
      setEditId(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSavingEdit(false); }
  };

  const applyBulk = async ({ subject, body, attachments }) => {
    try {
      if (subject || body) {
        const payload = { campaign_ids: Array.from(selected) };
        if (subject) payload.subject = subject;
        if (body) payload.email_body = body;
        const { data } = await api.post(`/campaign-batches/${job.batch_id}/bulk-edit`, payload);
        toast.success(`Aggiornati ${data.updated} contatti`);
      }
      if (attachments) {
        await api.put(`/campaign-batches/${job.batch_id}/attachments`, { attachments });
        if (attachments.length) toast.success(`Allegati campagna aggiornati (${attachments.length})`);
      }
      await loadBatch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Modifica fallita"); }
  };

  const doSend = async () => {
    setConfirmOpen(false); setSending(true);
    try {
      const ids = sendSelIds;
      const { data } = await api.post(`/campaign-batches/${job.batch_id}/send-selected`, { campaign_ids: ids });
      const sent = data?.sent ?? 0;
      if (sent > 0) {
        const mode = data?.email_mode === "draft" ? " (simulato: RESEND non configurato)" : "";
        toast.success(`Campagna inviata · ${sent} contatti${mode}`);
        await api.post(`/autopilot/jobs/${job._id}/resolve`, { outcome: "sent", sent_count: sent });
        onResolved(job._id);
      } else {
        toast.error(`Nessuna email inviata (0/${data?.total ?? ids.length}). Verifica gli indirizzi dei contatti e riprova.`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio fallito"); }
    finally { setSending(false); }
  };

  const onDiscarded = async (data, ids) => {
    if (data.job_closed) { onResolved(job._id); return; }
    setSelected((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; });
    onJobPatched?.(job._id, { counts: data.counts });
    await loadBatch(true);
  };

  if (!batch) {
    return <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento bozze…</div>;
  }

  return (
    <div className="space-y-3" data-testid="autopilot-batch-queue">
      <div className="rounded-xl border border-border">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="autopilot-select-all" />
            {selCount} selezionati / {sendable.length} inviabili
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <SelectionActions job={job} ids={selectedIds}
              onSaved={{ navigate: onSaved?.navigate, done: (ids) => { setSavedIds((s) => new Set([...s, ...ids])); onSaved?.reload?.(); } }}
              onDiscarded={onDiscarded} />
            <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)} disabled={selCount === 0} data-testid="autopilot-bulk-btn"
              className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><Wand2 className="mr-1 h-3.5 w-3.5" /> Modifica massiva</Button>
          </div>
        </div>
        <div className="divide-y divide-white/5">
          {msgs.map((m) => {
            const isOpen = expanded.has(m._id);
            const sent = m.status === "inviata";
            const noContact = !(m.recipient || "").trim();
            const editing = editId === m._id;
            const host = cleanHost(m.website);
            return (
              <div key={m._id} data-testid="autopilot-queue-row" className="p-3">
                <div className="flex items-center gap-2">
                  <Checkbox checked={selected.has(m._id)} disabled={sent}
                    onCheckedChange={() => toggleSel(m._id)} data-testid="autopilot-row-checkbox" />
                  <button type="button" onClick={() => toggleExp(m._id)} data-testid="autopilot-row-expand"
                    className="text-muted-foreground hover:text-foreground">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground" data-testid="autopilot-row-subject">{m.subject || m.lead_name || "(senza oggetto)"}</p>
                    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground/80">{m.lead_name}</span>
                      <span>·</span>
                      <span data-testid="autopilot-row-recipient">{m.recipient || "nessun contatto"}</span>
                      {noContact && !sent && <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">non inviabile</span>}
                      {savedIds.has(m._id) && <span data-testid="autopilot-row-saved-badge" className="inline-flex items-center gap-1 rounded-full bg-positive/10 px-1.5 py-0.5 text-[10px] text-positive"><Archive className="h-3 w-3" /> in {DEST_LABEL[job.module]}</span>}
                      {host && (<>
                        <span>·</span>
                        <a href={normUrl(m.website)} target="_blank" rel="noreferrer" data-testid="autopilot-row-website"
                          className="inline-flex items-center gap-1 text-brand hover:underline"><Globe className="h-3 w-3" />{host}</a>
                      </>)}
                    </p>
                  </div>
                  <Button size="sm" variant="secondary" data-testid="autopilot-row-preview"
                    onClick={() => setPreviewPayload({ subject: m.subject, body: m.email_body, nation: m.nation, recipient_name: m.lead_name, campaign_id: m._id })}
                    className="h-8 rounded-full border border-border bg-transparent px-2 text-xs hover:bg-brema-hover"><Eye className="h-3.5 w-3.5" /></Button>
                  {!sent && (
                    <Button size="sm" variant="secondary" data-testid="autopilot-row-edit" onClick={() => (editing ? setEditId(null) : startEdit(m))}
                      className="h-8 rounded-full border border-border bg-transparent px-2 text-xs hover:bg-brema-hover"><PenLine className="h-3.5 w-3.5" /></Button>
                  )}
                  <StatusBadge status={m.status} />
                </div>
                {editing ? (
                  <div className="mt-2 space-y-2 rounded-md bg-brema-bg p-3">
                    <div className="space-y-1.5"><Label className="text-xs">Oggetto</Label>
                      <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} data-testid="autopilot-edit-subject" className="h-8 text-xs" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Testo</Label>
                      <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={6} data-testid="autopilot-edit-body" className="text-xs" /></div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit} disabled={savingEdit} data-testid="autopilot-edit-save"
                        className="h-8 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">{savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Salva</>}</Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditId(null)} data-testid="autopilot-edit-cancel"
                        className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">Annulla</Button>
                    </div>
                  </div>
                ) : isOpen && (
                  <div className="mt-2 whitespace-pre-line rounded-md bg-brema-bg p-3 text-xs text-foreground" data-testid="autopilot-row-body">{m.email_body || "(nessun contenuto)"}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Button onClick={() => setConfirmOpen(true)} disabled={sending || sendSelCount === 0} data-testid="autopilot-approve-send-btn"
        className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
        {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Rocket className="mr-2 h-4 w-4" /> Approva e Invia Campagna ({sendSelCount} contatti)</>}
      </Button>

      <BulkEditDialog open={bulkOpen} onOpenChange={setBulkOpen} module={job.module} kind="email" count={selCount}
        contactedCount={contactedCount}
        initialAttachments={batch?.batch?.attachments || batch?.attachments || []} onApply={applyBulk} />
      <EmailPreviewDialog open={!!previewPayload} onOpenChange={(o) => { if (!o) setPreviewPayload(null); }} payload={previewPayload} />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="autopilot-confirm-dialog" className="border-border bg-brema-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Approvare e inviare la campagna?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per inviare {sendSelCount} {job.channel === "whatsapp" ? "messaggi WhatsApp" : "email"} ai contatti selezionati. Ogni email include il blocco privacy e il link di disiscrizione. L'azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="autopilot-confirm-cancel" className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} data-testid="autopilot-confirm-send" className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">Sì, approva e invia</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Ambassador queue (invio assistito) ---------------- */
function AmbassadorQueue({ job, onResolved, onSaved, onJobPatched }) {
  const [items, setItems] = useState(job.amb_items || []);
  const [selected, setSelected] = useState(() => new Set((job.amb_items || []).map((i) => i.ambassador_id)));
  const [expanded, setExpanded] = useState(() => new Set());
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [editId, setEditId] = useState(null);
  const [editMsg, setEditMsg] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [sending, setSending] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [ambAttachments, setAmbAttachments] = useState([]);
  const [previewPayload, setPreviewPayload] = useState(null);

  useEffect(() => {
    if ((!items || items.length === 0) && (job.amb_items || []).length) {
      setItems(job.amb_items);
      setSelected(new Set(job.amb_items.map((i) => i.ambassador_id)));
    }
    // eslint-disable-next-line
  }, [job.amb_items]);

  const selCount = items.filter((i) => selected.has(i.ambassador_id)).length;
  const selectedIds = items.filter((i) => selected.has(i.ambassador_id)).map((i) => i.ambassador_id);
  const onDiscarded = (data, ids) => {
    if (data.job_closed) { onResolved(job._id); return; }
    const gone = new Set(ids);
    setItems((arr) => arr.filter((x) => !gone.has(x.ambassador_id)));
    setSelected((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; });
    onJobPatched?.(job._id, { counts: data.counts });
  };
  const contactedCount = items.filter((i) => selected.has(i.ambassador_id) && i.already_contacted).length;
  const emailSelCount = items.filter((i) => selected.has(i.ambassador_id) && (i.email || "").trim()).length;
  const allSel = items.length > 0 && items.every((i) => selected.has(i.ambassador_id));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(items.map((i) => i.ambassador_id)));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExp = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const copyMsg = (txt) => { navigator.clipboard?.writeText(txt || ""); toast.success("Messaggio copiato"); };

  const startEdit = (it) => { setEditId(it.ambassador_id); setEditMsg(it.message || ""); setExpanded((s) => new Set(s).add(it.ambassador_id)); };
  const saveEdit = async (it) => {
    setSavingEdit(true);
    try {
      await api.patch(`/agent/jobs/${job._id}/amb-item/${it.ambassador_id}`, { message: editMsg });
      setItems((arr) => arr.map((x) => x.ambassador_id === it.ambassador_id ? { ...x, message: editMsg } : x));
      setEditId(null); toast.success("Messaggio aggiornato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSavingEdit(false); }
  };

  const applyBulk = async ({ body, attachments }) => {
    if (attachments) setAmbAttachments(attachments);
    if (body) {
      const targets = items.filter((i) => selected.has(i.ambassador_id));
      let ok = 0;
      for (const it of targets) {
        try { await api.patch(`/agent/jobs/${job._id}/amb-item/${it.ambassador_id}`, { message: body }); ok += 1; } catch { /* */ }
      }
      setItems((arr) => arr.map((x) => selected.has(x.ambassador_id) ? { ...x, message: body } : x));
      toast.success(`Messaggio applicato a ${ok} profili`);
    }
    if (attachments && attachments.length) toast.success(`Allegati impostati (${attachments.length}): inclusi nelle email inviate`);
    if (!body && !(attachments || []).length) toast.error("Inserisci il messaggio o aggiungi un allegato");
  };

  const sendOneEmail = async (it) => {
    if (!it.email) { toast.error("Nessuna email: usa il DM (copia + apri profilo)"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/ambassadors/send-email", { ambassador_id: it.ambassador_id, subject: `Collaborazione Brema Bike × ${it.name}`, body: it.message, attachments: ambAttachments });
      toast.success(data.sent ? `Email inviata a ${it.email}` : (data.note || "Invio simulato"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio fallito"); }
    finally { setSending(false); }
  };
  const sendSelectedEmails = async () => {
    const targets = items.filter((i) => selected.has(i.ambassador_id) && (i.email || "").trim());
    if (targets.length === 0) { toast.error("Seleziona almeno un profilo con email"); return; }
    setSending(true);
    let ok = 0;
    for (const it of targets) {
      try {
        const { data } = await api.post("/ambassadors/send-email", { ambassador_id: it.ambassador_id, subject: `Collaborazione Brema Bike × ${it.name}`, body: it.message, attachments: ambAttachments });
        if (data.sent) ok += 1;
      } catch { /* continue */ }
    }
    setSending(false);
    toast.success(`Email inviate: ${ok}/${targets.length}`);
  };

  return (
    <div className="space-y-2" data-testid="autopilot-amb-queue">
      <p className="rounded-lg bg-brema-bg p-2.5 text-xs text-muted-foreground">Invio assistito: i DM non partono in automatico. Copia il messaggio e aprilo dal profilo. Puoi inviare email solo ai profili che hanno un indirizzo.</p>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-2.5">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="autopilot-amb-select-all" />
          {selCount} selezionati ({emailSelCount} con email)
        </label>
        <div className="flex flex-wrap gap-1.5">
          <SelectionActions job={job} ids={selectedIds}
            onSaved={{ navigate: onSaved?.navigate, done: (ids) => { setSavedIds((s) => new Set([...s, ...ids])); onSaved?.reload?.(); } }}
            onDiscarded={onDiscarded} />
          <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)} disabled={selCount === 0} data-testid="autopilot-amb-bulk-btn"
            className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><Wand2 className="mr-1 h-3.5 w-3.5" /> Modifica massiva</Button>
          <Button size="sm" onClick={sendSelectedEmails} disabled={sending || emailSelCount === 0} data-testid="autopilot-amb-send-selected"
            className="h-8 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="mr-1 h-3.5 w-3.5" /> Invia email ({emailSelCount})</>}
          </Button>
        </div>
      </div>

      {items.map((it) => {
        const hasEmail = !!(it.email || "").trim();
        const isOpen = expanded.has(it.ambassador_id);
        const editing = editId === it.ambassador_id;
        return (
          <div key={it.ambassador_id} data-testid="autopilot-amb-item" className="rounded-xl border border-border bg-brema-bg p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Checkbox checked={selected.has(it.ambassador_id)} onCheckedChange={() => toggleSel(it.ambassador_id)} data-testid="autopilot-amb-checkbox" />
                {it.name} <span className="text-xs text-muted-foreground">@{it.handle}</span>
                {hasEmail ? <span className="ml-1 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] text-brand">email</span>
                          : <span className="ml-1 rounded-full bg-brema-card px-2 py-0.5 text-[10px] text-muted-foreground">solo DM</span>}
                {savedIds.has(it.ambassador_id) && <span data-testid="autopilot-amb-saved-badge" className="ml-1 inline-flex items-center gap-1 rounded-full bg-positive/10 px-2 py-0.5 text-[10px] text-positive"><Archive className="h-3 w-3" /> in Ambassador Salvati</span>}
              </label>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="secondary" data-testid="autopilot-amb-expand" onClick={() => toggleExp(it.ambassador_id)} className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">{isOpen ? <ChevronDown className="mr-1 h-3.5 w-3.5" /> : <ChevronRight className="mr-1 h-3.5 w-3.5" />} Testo</Button>
                <Button size="sm" variant="secondary" data-testid="autopilot-amb-edit" onClick={() => (editing ? setEditId(null) : startEdit(it))} className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><PenLine className="mr-1 h-3.5 w-3.5" /> Modifica</Button>
                <Button size="sm" variant="secondary" data-testid="autopilot-amb-copy" onClick={() => copyMsg(it.message)} className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><Copy className="mr-1 h-3.5 w-3.5" /> Copia</Button>
                {hasEmail && <Button size="sm" variant="secondary" data-testid="autopilot-amb-preview" onClick={() => setPreviewPayload({ subject: `Collaborazione Brema Bike × ${it.name}`, body: it.message, informal: true, target: "ambassador", contact_id: it.ambassador_id, recipient_name: it.name })} className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><Eye className="mr-1 h-3.5 w-3.5" /> Anteprima</Button>}
                {hasEmail && <Button size="sm" data-testid="autopilot-amb-send-email" disabled={sending} onClick={() => sendOneEmail(it)} className="h-8 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90"><Mail className="mr-1 h-3.5 w-3.5" /> Invia email</Button>}
                {it.url && <a href={it.url} target="_blank" rel="noreferrer" data-testid="autopilot-amb-profile" className="inline-flex h-8 items-center rounded-full border border-border px-3 text-xs text-brand hover:bg-brema-hover"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Profilo</a>}
              </div>
            </div>
            {editing ? (
              <div className="space-y-2">
                <Textarea value={editMsg} onChange={(e) => setEditMsg(e.target.value)} rows={5} data-testid="autopilot-amb-edit-textarea" className="text-xs" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveEdit(it)} disabled={savingEdit} data-testid="autopilot-amb-edit-save" className="h-8 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">{savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Salva</>}</Button>
                  <Button size="sm" variant="secondary" onClick={() => setEditId(null)} data-testid="autopilot-amb-edit-cancel" className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">Annulla</Button>
                </div>
              </div>
            ) : isOpen && (
              <p className="whitespace-pre-line rounded-md bg-brema-card p-2 text-xs text-foreground" data-testid="autopilot-amb-body">{it.message}</p>
            )}
          </div>
        );
      })}

      <BulkEditDialog open={bulkOpen} onOpenChange={setBulkOpen} module="ambassador" kind="dm" count={selCount}
        contactedCount={contactedCount}
        initialAttachments={ambAttachments} onApply={applyBulk} />
      <EmailPreviewDialog open={!!previewPayload} onOpenChange={(o) => { if (!o) setPreviewPayload(null); }} payload={previewPayload} />
    </div>
  );
}

/* ---------------- Single card (header + state routing) ---------------- */
function AutopilotCard({ job, onResolved, onSaved, onJobPatched }) {
  const Icon = MODULE_ICON[job.module] || Bot;
  const running = RUNNING.includes(job.status);
  const errored = job.status === "error";
  const ready = job.status === "ready";

  const dismiss = async () => {
    try { await api.post(`/autopilot/jobs/${job._id}/resolve`, { outcome: "dismissed" }); } catch { /* */ }
    onResolved(job._id);
  };

  return (
    <div data-testid="autopilot-card" className="rounded-2xl border border-brand/30 bg-brema-card p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Icon className="h-4 w-4 text-brand" />
          <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand">Autopilot</span>
          {MODULE_LABEL[job.module] || job.module}
        </div>
        {ready ? (
          <span className="text-[11px] text-muted-foreground" data-testid="autopilot-selective-hint">Spunta le righe e usa "Scarta selezionati" per rimuovere solo quelle</span>
        ) : (
          <button onClick={dismiss} data-testid="autopilot-dismiss-btn" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" /> Scarta proposta
          </button>
        )}
      </div>

      {job.plan?.summary && <p className="mb-2 rounded-lg bg-brema-bg p-2.5 text-xs text-muted-foreground" data-testid="autopilot-summary">{job.plan.summary}</p>}

      {running && (
        <div className="space-y-2" data-testid="autopilot-progress">
          <div className="flex items-center gap-2 text-sm text-foreground"><Loader2 className="h-4 w-4 animate-spin text-brand" /> {job.step_label || "Elaborazione…"}</div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-brema-bg"><div className="h-full bg-primary transition-all duration-500" style={{ width: `${job.progress || 5}%` }} /></div>
        </div>
      )}

      {errored && (
        <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 text-sm text-red-600 dark:text-red-300" data-testid="autopilot-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {job.error || "Esecuzione non riuscita"}
        </div>
      )}

      {ready && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-positive/10 px-2.5 py-1 text-positive"><CheckCircle2 className="h-3.5 w-3.5" /> Proposta pronta</span>
            <span className="rounded-full bg-brema-bg px-2.5 py-1 text-muted-foreground">Trovati: <b className="text-foreground">{job.counts?.found ?? 0}</b></span>
            <span className="rounded-full bg-brema-bg px-2.5 py-1 text-muted-foreground">Contattabili: <b className="text-foreground">{job.counts?.drafts ?? 0}</b></span>
            {job.counts?.without_contact > 0 && <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-amber-700 dark:text-amber-300">Senza contatto: {job.counts.without_contact}</span>}
            {job.discovery_report && (
              <span className="rounded-full bg-brema-bg px-2.5 py-1 text-muted-foreground" data-testid="autopilot-discovery-report">
                Qualificazione Brema ≥{job.discovery_report.threshold ?? 70}: <b className="text-foreground">{job.discovery_report.analyzed ?? 0}</b> analizzati · <b className="text-positive">{job.discovery_report.qualified ?? 0}</b> qualificati · {job.discovery_report.to_verify ?? 0} da verificare · {job.discovery_report.excluded ?? 0} esclusi
              </span>
            )}
          </div>
          {job.module === "ambassador"
            ? <AmbassadorQueue job={job} onResolved={onResolved} onSaved={onSaved} onJobPatched={onJobPatched} />
            : <BatchQueue job={job} onResolved={onResolved} onSaved={onSaved} onJobPatched={onJobPatched} />}
        </div>
      )}
    </div>
  );
}

/* ---------------- Parent list + polling ---------------- */
export default function AutopilotQueue({ module = null, onSaved = null }) {
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const pollRef = useRef(null);
  const patchJob = (id, patch) => setJobs((js) => js.map((j) => (j._id === id ? { ...j, ...patch } : j)));

  const load = async () => {
    try {
      const url = module ? `/autopilot/queue?module=${module}` : "/autopilot/queue";
      const { data } = await api.get(url);
      const incoming = data.jobs || [];
      setJobs((prev) => {
        const prevMap = new Map(prev.map((j) => [j._id, j]));
        return incoming.map((j) => {
          const old = prevMap.get(j._id);
          if (old && old.status === "ready" && j.status === "ready") return old;
          return j;
        });
      });
    } catch { /* keep last */ }
    finally { setLoaded(true); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  const removeJob = (id) => setJobs((js) => js.filter((j) => j._id !== id));

  if (!loaded || jobs.length === 0) return null;

  return (
    <div data-testid="autopilot-queue" className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand"><Bot className="h-4 w-4" /></span>
        <h3 className="text-sm font-bold uppercase tracking-wide text-foreground">Coda di approvazione Autopilot</h3>
        <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-semibold text-brand" data-testid="autopilot-queue-count">{jobs.length}</span>
      </div>
      {jobs.map((job) => <AutopilotCard key={job._id} job={job} onResolved={removeJob} onSaved={onSaved} onJobPatched={patchJob} />)}
    </div>
  );
}
